import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable, of, switchMap, throwError } from 'rxjs';
import { Pagination } from '~core/models/core.models';
import { ApiConstantsService } from './api-constants.service';
import {
  Receipt,
  ReceiptListParams,
  ReceiptValidationResponse
} from '~core/models/receipt.models';

@Injectable()
export class ReceiptService {
  private _apiConstantsService = inject(ApiConstantsService);
  private _httpClient = inject(HttpClient);

  readonly apiUrl = this._apiConstantsService.receiptsUrl;

  read(establishmentId: string): Observable<Receipt> {
    return this._httpClient.get<Receipt>(`${this.apiUrl}/${establishmentId}`);
  }

  list(params?: ReceiptListParams): Observable<Pagination<Receipt>> {
    return this._httpClient.get<Pagination<Receipt>>(this.apiUrl, {
      params: {
        ...params,
        sort: params?.sort || 'createdAt',
        desc: params?.desc || 'createdAt'
      } as Record<string, any>
    });
  }

  searchByReference(reference: string): Observable<Receipt> {
    return this._httpClient
      .get<ReceiptValidationResponse>(
        `${this._apiConstantsService.receiptMicroservice}/receipts/${reference}/verify`,
        {
          params: {
            skipJwtInterceptor: true
          }
        }
      )
      .pipe(
        switchMap(data => {
          if (!data.receipt_number) {
            return throwError(() => new HttpErrorResponse({ status: 404 }));
          }
          return of({
            receiptNumber: data.receipt_number,
            paymentReference: data.ref_paiement,
            terminalId: data.id_terminal,
            transactionId: data.id_transaction,
            ticketNumber: data.ticket_number,

            establishmentCode: data.code_etablissement,
            merchantName: data.nom_contribuable,
            merchantNCC: data.ncc_marchand,
            taxpayerName: data.nom_contribuable,
            clientName: data.name_client,
            clientNCC: data.ncc_client,

            amount: {
              amount:
                parseFloat(data.montant || '0') +
                parseFloat(data.fiscal_stamp || '0'),
              currency: 'XOF'
            } as any,
            totalHt: parseFloat(data.montant || '0'),
            fiscalStamp: {
              amount: parseFloat(data.fiscal_stamp || '0'),
              currency: 'XOF'
            } as any,
            stickerPrice: data.prix_sticker,

            ticketDetails: data.details_tickets,
            token: data.token,
            paymentMethod: data.mode_paiement,
            paymentOperator: data.operateur_paiement,
            averagePaymentNumber: data.num_moyen_paiement,
            receiptType: data.receipt_type,
            type: data.type,

            issuedAt: data.date_heure
          } as unknown as Receipt);
        })
      );
  }

  downloadReceipt(
    invoiceId: string,
    currency?: string
  ): Observable<HttpResponse<ArrayBuffer>> {
    let params = new HttpParams();

    if (currency) {
      params = params.set('currency', currency.toUpperCase());
    }

    return this._httpClient.get(`${this.apiUrl}/${invoiceId}/download`, {
      params,
      responseType: 'arraybuffer',
      observe: 'response'
    });
  }
}
